import { useCallback, useEffect, useMemo, useState } from "react";

import { Flex } from "@mantine/core";
import { IconDownload, IconLink, IconLock, IconLockOpen } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { Button, PublicLinkModal, Select, Text } from "components";
import { IRootState } from "config/redux";
import { AuthorityConstants, ServiceConstants } from "constant";
import { CreditService } from "services";
import { displayClasses } from "styles";
import { CreditResponse, CreditStatus } from "types";
import { AppUtils, UserUtils } from "utils";

const { ROLE_CR_C, ROLE_CR_REJECT_WK } = AuthorityConstants;
const {
  REQUEST_CREDIT_STATUS_CHANGE_ID,
  REQUEST_CREDIT_REQUEST_ID,
  REQUEST_CREDIT_AVAILABLE_STATUSES_ID,
  REQUEST_CREDIT_DOWNLOAD_ID,
  REQUEST_PENDING,
  REQUEST_CREDIT_LINK_CREATE_ID,
  REQUEST_CREDIT_LOCK_ID,
  REQUEST_CREDIT_UNLOCK_ID,
} = ServiceConstants;

const NEGATIVE_STATUSES: CreditStatus[] = ["REJECTED", "WITHDRAWN", "REJECTED_WK_TEAM"];
const POSITIVE_STATUSES: CreditStatus[] = ["SETTLED", "DISBURSED"];

interface CreditHeaderProps {
  credit: CreditResponse;
  onRequestDocumentation?: () => boolean;
}

const CreditHeader = ({ credit, onRequestDocumentation }: CreditHeaderProps) => {
  const { t } = useTranslation();
  const downloadServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CREDIT_DOWNLOAD_ID],
  );
  const lockServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CREDIT_LOCK_ID],
  );
  const unlockServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CREDIT_UNLOCK_ID],
  );
  const [linkVisible, setLinkVisible] = useState<boolean>(false);
  const [locked, setLocked] = useState<boolean>(credit.locked);

  useEffect(() => {
    AppUtils.resolveServiceObject(downloadServiceObject, REQUEST_CREDIT_DOWNLOAD_ID, async (val) => {
      const url = window.URL.createObjectURL(new Blob([val]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${credit.user.fullName}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    });
    AppUtils.resolveServiceObject(lockServiceObject, REQUEST_CREDIT_LOCK_ID, () => setLocked(true));
    AppUtils.resolveServiceObject(unlockServiceObject, REQUEST_CREDIT_UNLOCK_ID, () => setLocked(false));
  }, [downloadServiceObject, lockServiceObject, unlockServiceObject, credit.user.fullName]);

  const changeStatus = useCallback(
    async (status: CreditStatus | "REQUEST") => {
      if (status === "REQUEST") {
        if (onRequestDocumentation?.() === false) {
          return;
        }

        CreditService.creditRequestRequest(credit.id);
      } else {
        CreditService.creditStatusChange(credit.id, status);
      }
    },
    [credit.id, onRequestDocumentation],
  );

  const downloadAsZip = useCallback(() => CreditService.creditDownload(credit.id), [credit.id]);
  const lockCredit = useCallback(() => CreditService.creditLock(credit.id), [credit.id]);
  const unlockCredit = useCallback(() => CreditService.creditUnlock(credit.id), [credit.id]);

  return (
    <Flex justify={"space-between"} align={"center"} className={displayClasses.header}>
      <Flex>
        <Text size={"lg"} mr={"xs"}>
          {t("credit.current")}
        </Text>
        <Button onClick={downloadAsZip} size={"compact-xs"} variant={"transparent"}>
          <IconDownload color={"var(--mantine-color-gray-7)"} />
        </Button>
        <Button size={"compact-xs"} variant={"transparent"} onClick={() => setLinkVisible(true)}>
          <IconLink color={"var(--mantine-color-gray-7)"} />
        </Button>
        <PublicLinkModal
          opened={linkVisible}
          onClose={() => setLinkVisible(false)}
          create={() => CreditService.creditLinkCreate(credit.id)}
          requestId={REQUEST_CREDIT_LINK_CREATE_ID}
          t={t}
          link={credit.link}
          authority={ROLE_CR_C}
          object={"credit"}
        />
        {locked ? (
          <Button onClick={unlockCredit} size={"compact-xs"} variant={"transparent"}>
            <IconLockOpen size={23} color={"var(--mantine-color-gray-7)"} />
          </Button>
        ) : (
          <Button onClick={lockCredit} size={"compact-xs"} variant={"transparent"}>
            <IconLock size={23} color={"var(--mantine-color-gray-7)"} />
          </Button>
        )}
      </Flex>
      <Flex align={"center"}>
        <Text>{t("credit.status.label") + ":"}</Text>
        <CreditStatusLabel status={credit.status} />
        <CreditStatusButtons
          creditId={credit.id}
          status={credit.status}
          requestedAt={credit.requestedAt}
          onClick={changeStatus}
        />
      </Flex>
    </Flex>
  );
};

interface CreditStatusLabelProps {
  status: CreditResponse["status"];
}

const CreditStatusLabel = ({ status }: CreditStatusLabelProps) => {
  const { t } = useTranslation();

  if (NEGATIVE_STATUSES.includes(status)) {
    return (
      <Text ml={"xs"} c={"var(--mantine-color-error)"}>
        {t("credit.status.name." + status)}
      </Text>
    );
  }

  if (POSITIVE_STATUSES.includes(status)) {
    return (
      <Text ml={"xs"} c={"var(--mantine-primary-color-6)"}>
        {t("credit.status.name." + status)}
      </Text>
    );
  }

  return <Text ml={"xs"}>{t("credit.status.name." + status)}</Text>;
};

interface CreditStatusButtonsProps {
  creditId: number;
  status: CreditResponse["status"];
  requestedAt: CreditResponse["requestedAt"];
  onClick: (val: CreditStatus | "REQUEST") => void;
}

const CreditStatusButtons = ({ creditId, status, requestedAt, onClick }: CreditStatusButtonsProps) => {
  const { t } = useTranslation();
  const creditStatusServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CREDIT_STATUS_CHANGE_ID],
  );
  const creditRequestServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CREDIT_REQUEST_ID],
  );
  const availableStatusesServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CREDIT_AVAILABLE_STATUSES_ID + creditId],
  );
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);

  useEffect(() => {
    CreditService.creditAvailableStatuses(creditId);
  }, [creditId, status]);

  const isLoading = useMemo(
    () =>
      (creditRequestServiceObject && creditRequestServiceObject.status === REQUEST_PENDING) ||
      (creditStatusServiceObject && creditStatusServiceObject.status === REQUEST_PENDING) ||
      (availableStatusesServiceObject && availableStatusesServiceObject.status === REQUEST_PENDING),
    [creditRequestServiceObject, creditStatusServiceObject, availableStatusesServiceObject],
  );

  const availableStatuses = useMemo(() => {
    if (!Array.isArray(availableStatusesServiceObject?.data)) {
      return [];
    }

    return (availableStatusesServiceObject.data as CreditStatus[]).filter(
      (availableStatus) =>
        availableStatus !== "REJECTED_WK_TEAM" || UserUtils.hasAuthority(ROLE_CR_REJECT_WK),
    );
  }, [availableStatusesServiceObject]);

  useEffect(() => {
    if (!selectedStatus) {
      return;
    }

    if (!availableStatuses.includes(selectedStatus as CreditStatus)) {
      setSelectedStatus(null);
    }
  }, [availableStatuses, selectedStatus]);

  const selectOptions = useMemo(
    () =>
      availableStatuses.map((status) => ({
        value: status,
        label: t(`credit.status.name.${status}`),
      })),
    [availableStatuses, t],
  );

  return (
    <Flex ml={"sm"} gap={"sm"} align={"center"}>
      {!requestedAt && (
        <Button size={"compact-md"} onClick={() => onClick("REQUEST")} loading={isLoading}>
          {t("credit.status.action.REQUEST")}
        </Button>
      )}
      {selectOptions.length > 0 && (
        <Select
          value={selectedStatus}
          data={selectOptions}
          placeholder={t("credit.status.action.change", { defaultValue: "Promeni status" })}
          disabled={isLoading}
          clearable={false}
          searchable={false}
          w={240}
          onChange={(value) => {
            setSelectedStatus(value);
            if (value) {
              onClick(value as CreditStatus);
            }
          }}
        />
      )}
    </Flex>
  );
};

export default CreditHeader;
